import { uploadToS3 } from 'lane-shared/helpers';
import {
  AttachmentResponse,
  AttachmentEntityInput,
  AttachmentDataForUpload,
} from 'application-types';

import { createAttachment } from './createAttachment';
import { deleteAttachment } from './deleteAttachment';

export const uploadAttachment = async (
  entity: AttachmentEntityInput,
  attachmentAndThumbnail: AttachmentDataForUpload,
  options?: { parallelUploads?: boolean; deleteOnUploadFailure?: boolean }
): Promise<AttachmentResponse> => {
  const createdAttachment = await createAttachment({
    entity,
    ...attachmentAndThumbnail,
  });

  const hasThumbnail =
    attachmentAndThumbnail.thumbnail &&
    attachmentAndThumbnail.thumbnailBlob &&
    !!createdAttachment.thumbnailSignedUrl;

  try {
    if (options?.parallelUploads) {
      const uploads = [];

      if (createdAttachment.fileSignedUrl) {
        uploads.push(
          uploadToS3({
            signed: createdAttachment.fileSignedUrl,
            file: attachmentAndThumbnail.attachmentBlob,
            contentType: attachmentAndThumbnail.attachment.type,
          })
        );
      }

      if (hasThumbnail) {
        uploads.push(
          uploadToS3({
            signed: createdAttachment.thumbnailSignedUrl,
            file: attachmentAndThumbnail.thumbnailBlob,
            contentType: attachmentAndThumbnail.thumbnailBlob!.type,
          })
        );
      }

      await Promise.all(uploads);
    } else {
      if (createdAttachment.fileSignedUrl) {
        await uploadToS3({
          signed: createdAttachment.fileSignedUrl,
          file: attachmentAndThumbnail.attachmentBlob,
          contentType: attachmentAndThumbnail.attachment.type,
        });
      }

      if (hasThumbnail) {
        await uploadToS3({
          signed: createdAttachment.thumbnailSignedUrl,
          file: attachmentAndThumbnail.thumbnailBlob,
          contentType: attachmentAndThumbnail.thumbnailBlob!.type,
        });
      }
    }
  } catch (error) {
    // Compensating delete: remove the orphaned DB row so admins don't hit a
    // 403 for an attachment whose S3 object never landed (TS-38866). Gated so
    // FF-off preserves the previous behavior (row outlives a failed upload).
    if (options?.deleteOnUploadFailure) {
      try {
        await deleteAttachment({ attachmentId: createdAttachment.id });
      } catch {
        // Don't let a delete failure mask the original upload error — the
        // caller surfaces `error` to the user. Orphan row is retried by the
        // sweep/next submission; the upload error is the actionable one.
      }
    }

    throw error;
  }

  return createdAttachment;
};
